import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/sso',
      name: 'sso',
      component: () => import('@/views/SSOView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/',
      component: () => import('@/components/layout/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          redirect: '/datasets'
        },
        {
          path: 'datasets',
          name: 'datasets',
          component: () => import('@/views/DatasetsView.vue')
        },
        {
          path: 'datasets/new',
          name: 'dataset-create',
          component: () => import('@/views/DatasetCreateView.vue')
        },
        {
          path: 'datasets/:code',
          name: 'dataset-detail',
          component: () => import('@/views/DatasetDetailView.vue'),
          props: true
        },
        {
          path: 'models',
          name: 'models',
          component: () => import('@/views/ModelsView.vue')
        },
        {
          path: 'models/new',
          name: 'model-create',
          component: () => import('@/views/ModelCreateView.vue')
        },
        {
          path: 'models/:code',
          name: 'model-detail',
          component: () => import('@/views/ModelDetailView.vue'),
          props: true
        },
        {
          path: 'models/:code/predictions',
          name: 'model-predictions',
          component: () => import('@/views/ModelPredictionsView.vue'),
          props: true
        },
        {
          path: 'jobs',
          name: 'jobs',
          component: () => import('@/views/JobsView.vue')
        }
      ]
    },
    {
      path: '/admin',
      component: () => import('@/components/layout/AdminLayout.vue'),
      meta: { requiresAuth: true, requiresPlatformAdmin: true },
      children: [
        {
          path: '',
          redirect: '/admin/tenants'
        },
        {
          path: 'tenants',
          name: 'admin-tenants',
          component: () => import('@/views/admin/AdminTenantsView.vue')
        },
        {
          path: 'users',
          name: 'admin-users',
          component: () => import('@/views/admin/AdminUsersView.vue')
        },
        {
          path: 'users/new',
          name: 'admin-users-create',
          component: () => import('@/views/admin/AdminUsersCreateView.vue')
        },
        {
          path: 'tenants/new',
          name: 'admin-tenant-create',
          component: () => import('@/views/admin/AdminTenantCreateView.vue')
        },
        {
          path: 'tenants/:code',
          name: 'admin-tenant-detail',
          component: () => import('@/views/admin/AdminTenantDetailView.vue'),
          props: true
        },
        {
          path: 'tenants/:code/users',
          name: 'admin-tenant-users',
          component: () => import('@/views/admin/AdminTenantUsersView.vue'),
          props: true
        },
        {
          path: 'tenants/:code/users/new',
          name: 'admin-user-create',
          component: () => import('@/views/admin/AdminUserCreateView.vue'),
          props: true
        },
        {
          path: 'tenants/:code/users/:userCode',
          name: 'admin-user-edit',
          component: () => import('@/views/admin/AdminUserEditView.vue'),
          props: true
        }
      ]
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/datasets'
    }
  ]
})

// Navigation guard for authentication
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Initialize auth state if not done
  if (!authStore.initialized) {
    await authStore.initialize()
  }

  const requiresAuth = to.meta.requiresAuth !== false
  const requiresPlatformAdmin = to.meta.requiresPlatformAdmin === true

  if (requiresAuth && !authStore.isAuthenticated) {
    next('/login')
  } else if (requiresPlatformAdmin && !authStore.isPlatformAdmin) {
    // Non-admins cannot access admin routes
    next('/datasets')
  } else if (!requiresAuth && authStore.isAuthenticated && (to.name === 'login' || to.name === 'sso')) {
    // Redirect authenticated users away from login
    next(authStore.isPlatformAdmin ? '/admin/tenants' : '/datasets')
  } else {
    next()
  }
})

export default router
